import FormEngine from '@form-engine/core-application';
import FormEngineFormSelect from '@form-engine/core-application/components/select';
import { useFormElementDatasourceData } from '@form-engine/core-application/hooks/use-datasource';
import { useFormEngineElement } from '@form-engine/core-application/hooks/use-element';
import { useFormEngineElementDisabled } from '@form-engine/core-application/hooks/use-element-disabled';
import { useFormEngineElementRequired } from '@form-engine/core-application/hooks/use-element-required';
import { useFormContext } from '@form-engine/core-application/hooks/use-form';
import {
    useFormElementSession,
    useFormSessionLocals,
} from '@form-engine/core-application/hooks/use-formsession';
import { compile } from '@form-engine/core-util/template';
import React, { useCallback } from 'react';
import {
    FloatingLabel,
    FormControl,
    FormGroup,
    FormLabel,
} from 'react-bootstrap';

export const CONTROL_TYPES = {
    NATIVE: 'native',
    ADVANCED: 'advanced',
} as const;

export type ControlTypes = (typeof CONTROL_TYPES)[keyof typeof CONTROL_TYPES];

export const apiName = 'FormEngineSelectElement' as const;

export default Object.assign(
    function FormEngineSelectElement() {
        const {
            state: { showValidFeedback = true },
        } = useFormContext();

        const { state: element } = useFormEngineElement();
        if (!element || element.type !== apiName)
            throw new Error('tried to render select with invalid definition');
        const {
            name = '',
            label = '',
            floatingLabel = false,
            searchable = true,
            controlType = CONTROL_TYPES.NATIVE,
            showDefaultOption = false,
            defaultOptionLabel = FormEngine.i18n.c(
                'select-element.default-option'
            ),
            placeholder = FormEngine.i18n.c('select-element.default-option'),
        } = element;

        const renderFloatingLabel = label && floatingLabel;

        const disabled = useFormEngineElementDisabled();

        const { state: locals } = useFormSessionLocals();

        const required = useFormEngineElementRequired();

        const [session, setSession] = useFormElementSession();
        const handleChange = useCallback(
            (e: React.ChangeEvent) => {
                const value = (e.target as HTMLSelectElement).value;
                setSession({ ...session, isTouched: true, value });
            },
            [setSession, session]
        );
        const datasourceResult = useFormElementDatasourceData();
        let options = datasourceResult.options.map((option) => {
            let label, value;
            if (Array.isArray(option)) {
                [value, label] = option;
            } else {
                label = value = option;
            }

            value = compile(value || '', locals, '');
            label = compile(label || value, locals, '');

            return { value, label };
        });

        // introduces an empty option on native selects, as a means to clear
        // optional values. on advanced selects, this is not needed
        if (controlType === CONTROL_TYPES.NATIVE && showDefaultOption) {
            options = [{ label: defaultOptionLabel, value: '' }, ...options];
        }

        let control = (
            <FormEngineFormSelect
                id={element.id}
                name={name}
                disabled={datasourceResult.loading || disabled}
                value={String(session.value ?? '')}
                onChange={handleChange}
                required={required}
                isValid={
                    showValidFeedback && session.isTouched && !session.error
                }
                isInvalid={Boolean(session.isTouched && session.error)}
            >
                {options.map(({ value, label }, key) => {
                    return (
                        <option key={key} value={value}>
                            {label || value}
                        </option>
                    );
                })}
            </FormEngineFormSelect>
        );

        if (controlType === CONTROL_TYPES.ADVANCED) {
            // we only need feedback classes for react-select (CONTROL_TYPES.ADVANCED),
            // since react-bootstrap-component's select comes with that out of the box
            let feedbackClassNames: string | undefined;

            if (showValidFeedback && session.isTouched) {
                feedbackClassNames = session.error ? 'is-invalid' : 'is-valid';
            }

            control = (
                <FormEngineFormSelect
                    id={element.id}
                    name={name}
                    placeholder={placeholder}
                    isDisabled={datasourceResult.loading || disabled}
                    value={String(session.value ?? '')}
                    onChange={handleChange}
                    required={required}
                    isInvalid={Boolean(session.isTouched && session.error)}
                    className={feedbackClassNames}
                    options={options}
                    isSearchable={searchable}
                />
            );
        }

        const error = session.isTouched &&
            element.showError &&
            session.error && (
                <FormControl.Feedback type="invalid" tooltip={false}>
                    {session.error}
                </FormControl.Feedback>
            );

        return (
            <FormGroup className="select-formgroup">
                <>
                    {renderFloatingLabel ? (
                        <FloatingLabel label={label} controlId={element.id}>
                            <>
                                <control.type
                                    {...control.props}
                                    id={undefined}
                                />
                                {error}
                            </>
                        </FloatingLabel>
                    ) : (
                        <>
                            {label && (
                                <FormLabel htmlFor={element.id}>
                                    {label}
                                </FormLabel>
                            )}
                            {control}
                            {error}
                        </>
                    )}
                </>
            </FormGroup>
        );
    },
    { apiName, isField: true, enabled: true }
);
